import React, { useEffect, useState, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import { styled } from "@mui/material/styles";
import { axiosClient } from "../AxiosClient";
import { PRAMI_COLORS } from "./certificadosTheme";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  boxShadow: "0 1px 3px rgba(22, 30, 85, 0.06)",
}));

const ButtonPrincipal = styled(Button)(({ theme }) => ({
  flexGrow: 0,
  borderRadius: "20px",
  gap: "10px",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: PRAMI_COLORS.hoverMint,
    color: PRAMI_COLORS.drawerNavy,
  },
}));

const SectionTitle = styled(Typography)({
  color: PRAMI_COLORS.primary,
  fontWeight: 600,
});

const surfacePaperSx = {
  bgcolor: "#fff",
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(22, 30, 85, 0.06)",
};

const tableHeaders = [
  "Nombre cliente",
  "Cédula",
  "Tipo de documento",
  "Fecha de generación",
  "Código de trazabilidad",
  "Acción",
];

const PAGE_SIZE = 20;

const documentTypeBadge = (type) => {
  if (type === "PAZ_Y_SALVO") {
    return (
      <Chip label="Paz y salvo" size="small" color="success" sx={{ fontWeight: 600 }} />
    );
  }
  if (type === "ESTADO_DE_CUENTA") {
    return (
      <Chip label="Estado de cuenta" size="small" color="primary" sx={{ fontWeight: 600 }} />
    );
  }
  return <Chip label={type ?? "-"} size="small" variant="outlined" sx={{ borderColor: PRAMI_COLORS.surfaceBorder }} />;
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return "-";
  try {
    return new Date(isoStr).toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
};

export default function TrazabilidadDocumentos({ refreshSignal = 0 }) {
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);

  // Refs para refrescar el listado tras una emisión sin disparar fetches duplicados.
  const pageRef = useRef(0);
  const searchRef = useRef("");

  const [loading, setLoading] = useState(false);
  const [downloadingUuid, setDownloadingUuid] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const fetchDocuments = useCallback(
    (currentPage, documentNumber) => {
      setLoading(true);
      const trimmed = documentNumber?.trim();
      const endpoint = trimmed
        ? `/v1/portal/document-records/by-document-number/${encodeURIComponent(trimmed)}`
        : `/v1/portal/document-records`;

      axiosClient
        .get(endpoint, {
          params: {
            page: currentPage,
            size: PAGE_SIZE,
            sort: "generatedAt,desc",
          },
        })
        .then((response) => {
          const data = response.data;
          setRows(data.content || []);
          setTotalElements(data.totalElements ?? 0);
        })
        .catch((error) => {
          console.error(error);
          showSnackbar("Error al consultar los documentos. Intenta de nuevo.", "error");
          setRows([]);
          setTotalElements(0);
        })
        .finally(() => setLoading(false));
    },
    [showSnackbar]
  );

  useEffect(() => {
    fetchDocuments(0, "");
  }, [fetchDocuments]);

  // Refresca el listado manteniendo página y búsqueda actuales tras cada emisión.
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) {
      firstRefresh.current = false;
      return;
    }
    fetchDocuments(pageRef.current, searchRef.current);
  }, [refreshSignal, fetchDocuments]);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setActiveSearch(trimmed);
    setPage(0);
    pageRef.current = 0;
    searchRef.current = trimmed;
    fetchDocuments(0, trimmed);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setActiveSearch("");
    setPage(0);
    pageRef.current = 0;
    searchRef.current = "";
    fetchDocuments(0, "");
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    pageRef.current = newPage;
    fetchDocuments(newPage, activeSearch);
  };

  const handleDownload = (record) => {
    setDownloadingUuid(record.uuid);
    axiosClient
      .get(`/v1/portal/document-records/${record.uuid}/download-url`, {
        params: { asAttachment: true },
      })
      .then((response) => {
        const url = response.data?.url;
        if (url) {
          window.open(url, "_blank");
        } else {
          showSnackbar("No se recibió URL de descarga.", "warning");
        }
      })
      .catch((error) => {
        console.error(error);
        showSnackbar("Error al obtener la URL de descarga. Intenta de nuevo.", "error");
      })
      .finally(() => setDownloadingUuid(null));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Item>
          <SectionTitle variant="h6" sx={{ mb: 0.5 }}>
            Trazabilidad de documentos generados
          </SectionTitle>
          <Typography variant="body2" color="text.secondary">
            Paz y salvos y estados de cuenta ya emitidos; búsqueda por cédula y descarga vía enlace firmado.
          </Typography>
        </Item>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2, ...surfacePaperSx }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <TextField
              label="Buscar por número de cédula"
              variant="outlined"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              sx={{ width: 340 }}
              InputProps={{
                endAdornment: activeSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} aria-label="Limpiar búsqueda">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <ButtonPrincipal
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              disabled={loading}
            >
              Buscar
            </ButtonPrincipal>
          </Stack>
          {activeSearch ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Mostrando resultados para cédula:{" "}
              <strong style={{ color: PRAMI_COLORS.primary }}>{activeSearch}</strong>
            </Typography>
          ) : null}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2, ...surfacePaperSx }}>
          <Divider sx={{ mb: 2 }}>
            <Chip
              label={`${totalElements} documento${totalElements !== 1 ? "s" : ""} encontrado${totalElements !== 1 ? "s" : ""}`}
              color="primary"
              size="small"
            />
          </Divider>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: PRAMI_COLORS.primary }}>
                  {tableHeaders.map((h) => (
                    <TableCell key={h} sx={{ color: PRAMI_COLORS.primaryContrast, fontWeight: 600 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {activeSearch
                          ? "No se encontraron documentos para esa cédula."
                          : "No hay documentos registrados."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((record) => (
                    <TableRow key={record.uuid} hover>
                      <TableCell>{record.clientName ?? "-"}</TableCell>
                      <TableCell>{record.documentNumber ?? "-"}</TableCell>
                      <TableCell>{documentTypeBadge(record.documentType)}</TableCell>
                      <TableCell>{formatDateTime(record.generatedAt)}</TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={record.traceabilityCode ?? "-"} placement="top">
                          <span>{record.traceabilityCode ?? "-"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Descargar documento">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(record)}
                              disabled={downloadingUuid === record.uuid}
                              sx={{
                                color: PRAMI_COLORS.primary,
                                "&:hover": { backgroundColor: "rgba(22, 30, 85, 0.08)" },
                              }}
                            >
                              {downloadingUuid === record.uuid ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            sx={{
              borderTop: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
              mt: 1,
              "& .MuiTablePagination-toolbar": { color: "text.secondary" },
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </Paper>
      </Grid>

      <Backdrop
        sx={{
          color: PRAMI_COLORS.primaryContrast,
          bgcolor: "rgba(22, 30, 84, 0.45)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}
